import type {
    BillingAccountNumberGetCustomerDetailsParams,
    GetCustomerDetailsParams,
    PhoneNumberGetCustomerDetailsParams,
} from "./types";

import { getHashedValue, removeCountryCode } from "../../../../../shared-utils";

export function isValidPhoneNumberSearchType(
    params: GetCustomerDetailsParams,
): params is PhoneNumberGetCustomerDetailsParams {
    return params.searchType === "phoneNumber" && (params as PhoneNumberGetCustomerDetailsParams).mdn != null;
}

export function isValidBillingAccountNumberAndPinSearchType(
    params: GetCustomerDetailsParams,
): params is BillingAccountNumberGetCustomerDetailsParams {
    return (
        params.searchType === "billingAccountNumberAndPin" &&
        (params as BillingAccountNumberGetCustomerDetailsParams).billingAccountNumber != null
    );
}

/**
 * Pure request-transform for the `v3/customer/details` POST body.
 *
 * Two source quirks live here and are worth protecting with unit tests:
 *  - `hashedMdn` is derived from `modelResultsMdn` (the IVR-sourced MDN), never from the search
 *    `mdn` the agent typed, and is omitted entirely when `modelResultsMdn` is absent.
 *  - `customerVerificationMethod` switches on `pin` presence: "PIN" when a pin is supplied,
 *    "BANV" otherwise.
 *
 * Returns `null` when neither valid search shape is satisfied — the caller must treat that as
 * "make no request".
 */
export const buildCustomerDetailsRequest = async (
    params: GetCustomerDetailsParams,
): Promise<Record<string, unknown> | null> => {
    const { modelResultsMdn, partner, sessionId, searchType } = params;
    const formattedMdn = modelResultsMdn && removeCountryCode(modelResultsMdn);
    const hashedMdn = formattedMdn && (await getHashedValue(formattedMdn));

    if (isValidPhoneNumberSearchType(params)) {
        return {
            partner,
            searchType,
            sessionId,
            criteria: {
                phoneNumber: removeCountryCode(params.mdn),
            },
            hashedMdn,
        };
    } else if (isValidBillingAccountNumberAndPinSearchType(params)) {
        return {
            partner,
            searchType,
            criteria: {
                billingAccountNumber: params.billingAccountNumber,
                customerVerificationMethod: params.pin ? "PIN" : "BANV",
                pin: params.pin,
            },
            hashedMdn,
        };
    }

    return null;
};
